import { STRUCTURE_GRID_SIZE } from 'data/constants';
import {
  AirHandlerTypeCodeType,
  ConcealedDuctLocationType,
  GridSpaceDataType,
  LevelType,
  LineSetPointType,
  LineSetType,
  PositionType
} from 'data/types';
import { nanoid } from 'nanoid';
import { getGroundLevelIndex } from 'utils/3d';
import { AIR_HANDLER_GRID_SQUARES } from 'utils/air-handler-grid-squares';
import {
  getPointHeightFeet,
  getSegmentHalf,
  getTransitionPointIndex
} from 'utils/3d-lineset-geometry';
import {
  buildLinesetRoute,
  computeVerticalRunsFeet
} from '@alpinehomeair/common';
import { GridSpace } from 'utils/lineset-geometry';
import { toLinesetLevels } from 'utils/lineset-level-adapter';
import { resolveLinesetPointLevels } from 'utils/lineset-point-levels';
import { findSpaceById } from 'utils/structure';

export { findCrossFloorLinesetPath, findLinesetPath } from 'utils/lineset-path';

export function calculateLinesetLengthFromPoints(
  lineset: LineSetType,
  structure: LevelType[] = []
): number {
  if (lineset.points.length < 2) return 0;

  const transitionPointIndex = getTransitionPointIndex(lineset);

  let totalLength = 0;
  for (let i = 0; i < lineset.points.length - 1; i += 1) {
    const current = lineset.points[i];
    const next = lineset.points[i + 1];
    const half = getSegmentHalf(i, transitionPointIndex);
    const dx = next.positionX - current.positionX;
    const dy = next.positionY - current.positionY;
    const dz =
      getPointHeightFeet(next, half, lineset.airHandlerTypeCode) -
      getPointHeightFeet(current, half, lineset.airHandlerTypeCode);
    totalLength += Math.hypot(dx, dy, dz);
  }

  if (structure.length > 0) {
    totalLength += computeVerticalRunsFeet(
      resolveLinesetPointLevels(lineset),
      toLinesetLevels(structure),
      lineset.airHandlerTypeCode
    );
  }

  return Math.round(totalLength);
}

export {
  calculatePathLengthFromPoints,
  countAxisChanges
} from 'utils/path-geometry';

export function getLinesetLengthForZone(
  lineSets: LineSetType[],
  condenserUniqueId: string,
  zoneItemUniqueIds: string[],
  fallbackDistance: number,
  structure: LevelType[] = []
): number {
  const lineset = lineSets.find(
    (ls) =>
      ls.condenserUniqueId === condenserUniqueId &&
      zoneItemUniqueIds.includes(ls.airHandlerUniqueId)
  );

  if (!lineset) return fallbackDistance;

  const calculatedLength = calculateLinesetLengthFromPoints(lineset, structure);
  return calculatedLength > 0 ? calculatedLength : fallbackDistance;
}

type BuildLinesetCondenserInput = {
  uniqueId: string;
  levelUniqueId: string | undefined;
  position: { x: number; y: number };
};

type BuildLinesetAirHandlerInput = {
  uniqueId: string;
  levelUniqueId: string | undefined;
  position: { x: number; y: number };
  targetSpaceId: string | undefined;
  airHandlerTypeCode?: AirHandlerTypeCodeType;
  concealedLocation?: ConcealedDuctLocationType | null;
};

export function toPathfindingSpaces(
  gridSpaces: GridSpaceDataType[],
  structure: LevelType[]
): GridSpace[] {
  return gridSpaces.map((space) => {
    const spaceData = findSpaceById(structure, space.spaceId);
    const verticesPixels = space.vertices;
    const verticesFeet = verticesPixels?.map((v) => ({
      x: v.x / STRUCTURE_GRID_SIZE,
      y: v.y / STRUCTURE_GRID_SIZE
    }));
    return {
      id: space.id,
      spaceId: space.spaceId,
      openTo: spaceData?.openTo,
      conditioned: spaceData?.conditioned,
      position: {
        x: space.position.x / STRUCTURE_GRID_SIZE,
        y: space.position.y / STRUCTURE_GRID_SIZE,
        width: space.position.width / STRUCTURE_GRID_SIZE,
        height: space.position.height / STRUCTURE_GRID_SIZE
      },
      vertices: verticesFeet
    };
  });
}

function equipmentCenter(position: PositionType): PositionType {
  return {
    x: position.x + AIR_HANDLER_GRID_SQUARES / 2,
    y: position.y + AIR_HANDLER_GRID_SQUARES / 2
  };
}

export function buildLinesetForPair(
  condenser: BuildLinesetCondenserInput,
  airHandler: BuildLinesetAirHandlerInput,
  structure: LevelType[],
  getGridSpacesForLevel: (levelIndex: number) => GridSpaceDataType[],
  configurationId: string
): LineSetType {
  const condenserLevelIsKnown = structure.some(
    (level) => level.uniqueId === condenser.levelUniqueId
  );
  const resolvedCondenser: BuildLinesetCondenserInput = condenserLevelIsKnown
    ? condenser
    : {
        ...condenser,
        levelUniqueId: structure[getGroundLevelIndex(structure)]?.uniqueId
      };

  const linesetUniqueId = nanoid(5);
  const condenserCenter = equipmentCenter(resolvedCondenser.position);
  const airHandlerCenter = equipmentCenter(airHandler.position);

  const isCrossFloor =
    !!resolvedCondenser.levelUniqueId &&
    !!airHandler.levelUniqueId &&
    resolvedCondenser.levelUniqueId !== airHandler.levelUniqueId;

  const chosenCandidate = buildLinesetRoute({
    condenserCenter,
    airHandlerCenter,
    condenserLevelUniqueId: resolvedCondenser.levelUniqueId,
    airHandlerLevelUniqueId: airHandler.levelUniqueId,
    airHandlerTargetSpaceId: airHandler.targetSpaceId,
    airHandlerTypeCode: airHandler.airHandlerTypeCode,
    concealedLocation: airHandler.concealedLocation,
    levels: toLinesetLevels(structure),
    getGridSpacesForLevel: (levelUniqueId) => {
      const levelIndex = structure.findIndex(
        (level) => level.uniqueId === levelUniqueId
      );
      if (levelIndex < 0) return [];
      return toPathfindingSpaces(getGridSpacesForLevel(levelIndex), structure);
    }
  });

  const linesetPoints: LineSetPointType[] = chosenCandidate.points.map(
    (point, idx) => ({
      ...point,
      uniqueId: nanoid(5),
      lineSetUniqueId: linesetUniqueId,
      positionX: point.x,
      positionY: point.y,
      indexNumber: idx,
      levelUniqueId: chosenCandidate.pointLevels[idx]
    })
  );

  return {
    uniqueId: linesetUniqueId,
    configurationUniqueId: configurationId,
    condenserUniqueId: resolvedCondenser.uniqueId,
    airHandlerUniqueId: airHandler.uniqueId,
    airHandlerTypeCode: airHandler.airHandlerTypeCode,
    condenserLevelUniqueId: resolvedCondenser.levelUniqueId,
    airHandlerLevelUniqueId: airHandler.levelUniqueId,
    isCrossFloor,
    addWire: false,
    valid: true,
    modified: true,
    points: linesetPoints
  };
}
